import { type Ref, ref } from 'vue';

import { STORIES_CONFIG } from '@/constants';

interface PauseResumeState {
  isPaused: Ref<boolean>;
  pauseStartTime: Ref<number | null>;
  remainingDuration: Ref<number | null>;
  originalDuration: Ref<number | null>;
  pauseProgress: Ref<number | null>;
  currentSlideIndex: Ref<number | null>;
  progressBarStartTime: Ref<number | null>;
}

export function useStoriesPauseResume(
  progressBars: Ref<(HTMLElement | null)[]>,
  getProgressBarWidth: (bar: HTMLElement) => number,
  setProgressBarWidth: (bar: HTMLElement, width: number) => void,
) {
  const isPaused = ref(false);
  const pauseStartTime = ref<number | null>(null);
  const remainingDuration = ref<number | null>(null);
  const originalDuration = ref<number | null>(null);
  const pauseProgress = ref<number | null>(null);
  const currentSlideIndex = ref<number | null>(null);
  const progressBarStartTime = ref<number | null>(null);

  const calculateRemainingDuration = (
    originalDurationValue: number,
    remainingDurationValue: number | null,
    progressBarStartTimeValue: number | null,
  ): number => {
    if (!progressBarStartTimeValue) {
      return originalDurationValue; // Animation hasn't started yet
    }

    const elapsed = Date.now() - progressBarStartTimeValue;
    const baseDuration =
      remainingDurationValue !== null &&
      remainingDurationValue < originalDurationValue
        ? remainingDurationValue
        : originalDurationValue;

    return Math.max(0, baseDuration - elapsed);
  };

  const validatePauseConditions = (
    slideIndex: number,
    swiperRef: any,
  ): boolean => {
    if (isPaused.value) {
      return false; // Don't pause if already paused
    }

    if (!swiperRef || swiperRef.realIndex !== slideIndex) {
      return false; // Verify this is the active slide
    }

    const progressBarElement = progressBars.value[slideIndex];
    if (!progressBarElement) {
      return false; // Bar doesn't exist
    }

    if (remainingDuration.value === null || originalDuration.value === null) {
      return false; // Animation hasn't started yet
    }

    return true;
  };

  const applyPauseState = (
    progressBarElement: HTMLElement,
    currentWidthPercent: number,
  ) => {
    progressBarElement.style.transition = 'none';
    setProgressBarWidth(progressBarElement, currentWidthPercent);
    isPaused.value = true;
    pauseStartTime.value = Date.now();
  };

  const pauseProgressBar = (
    slideIndex: number,
    swiperRef: any,
    clearTimeoutFn: () => void,
  ): void => {
    if (!validatePauseConditions(slideIndex, swiperRef)) {
      return;
    }

    const progressBarElement = progressBars.value[slideIndex];
    if (!progressBarElement) {
      return;
    }

    const currentWidthPercent = getProgressBarWidth(progressBarElement);
    pauseProgress.value = currentWidthPercent;

    const calculatedRemaining = calculateRemainingDuration(
      originalDuration.value!,
      remainingDuration.value,
      progressBarStartTime.value,
    );

    remainingDuration.value = calculatedRemaining;

    clearTimeoutFn();

    applyPauseState(progressBarElement, currentWidthPercent);
  };

  const validateResumeConditions = (slideIndex: number): boolean => {
    if (!isPaused.value) {
      return false;
    }

    if (
      currentSlideIndex.value === null ||
      currentSlideIndex.value === undefined
    ) {
      return false;
    }

    if (remainingDuration.value === null || pauseProgress.value === null) {
      return false;
    }

    const progressBarElement = progressBars.value[slideIndex];
    if (!progressBarElement) {
      return false;
    }

    return true;
  };

  const applyResumeAnimation = (
    progressBarElement: HTMLElement,
    startWidth: number,
    duration: number,
  ) => {
    progressBarElement.style.transition = 'none';
    setProgressBarWidth(progressBarElement, startWidth);

    // Force reflow to ensure the reset is applied
    void progressBarElement.offsetWidth;

    // Set the start time immediately (will be updated when animation actually starts)
    const resumeStartTime = Date.now();

    setTimeout(() => {
      progressBarElement.style.transition = `width ${duration}ms linear`;
      progressBarElement.style.width = '100%';
      // Update to actual animation start time
      progressBarStartTime.value = Date.now();
    }, STORIES_CONFIG.PROGRESS_BAR_RESET_DELAY_MS);

    // Return the resume start time for timeout calculation
    return resumeStartTime;
  };

  const resumeProgressBar = (
    slideIndex: number,
    swiperRef: any,
    scheduleTimeout: (duration: number) => number,
  ): number | null => {
    if (!validateResumeConditions(slideIndex)) {
      return null;
    }

    // Verify this is still the active slide
    if (!swiperRef || swiperRef.realIndex !== slideIndex) {
      return null;
    }

    const progressBarElement = progressBars.value[slideIndex];
    if (!progressBarElement) {
      return null;
    }

    const duration = remainingDuration.value!;
    const startWidth = pauseProgress.value!;

    if (duration <= 0) {
      isPaused.value = false;
      pauseStartTime.value = null;
      if (swiperRef) {
        swiperRef.slideNext(STORIES_CONFIG.SWIPER_TRANSITION_SPEED_MS);
      }
      return null;
    }

    const resumeStartTime = applyResumeAnimation(
      progressBarElement,
      startWidth,
      duration,
    );

    // Schedule next slide transition - account for reset delay
    // Animation starts after resetDelay, then takes duration to complete
    // Total time from resume call = resetDelay + duration
    const totalDelay = STORIES_CONFIG.PROGRESS_BAR_RESET_DELAY_MS + duration;
    const timeoutId = scheduleTimeout(totalDelay);

    isPaused.value = false;
    pauseStartTime.value = null;

    return timeoutId;
  };

  const resetPauseState = () => {
    isPaused.value = false;
    pauseStartTime.value = null;
    pauseProgress.value = null;
    remainingDuration.value = null;
    originalDuration.value = null;
    progressBarStartTime.value = null;
  };

  const setSlideState = (
    slideIndex: number,
    duration: number,
    startTime: number,
  ) => {
    isPaused.value = false;
    pauseStartTime.value = null;
    pauseProgress.value = null;
    currentSlideIndex.value = slideIndex;
    originalDuration.value = duration;
    remainingDuration.value = duration;
    progressBarStartTime.value = startTime;
  };

  return {
    isPaused,
    pauseStartTime,
    remainingDuration,
    originalDuration,
    pauseProgress,
    currentSlideIndex,
    progressBarStartTime,
    pauseProgressBar,
    resumeProgressBar,
    resetPauseState,
    setSlideState,
  };
}
